/**
 * useWorkspaceDetail.ts
 *
 * Fetches all data for the Workspace Research Hub detail page from
 * GET /api/workspaces/:id/detail (workspaceDetailController).
 *
 * Usage:
 *   const { data, loading, error, refetch } = useWorkspaceDetail(workspaceId);
 *
 * Contract:
 *   - data is null while loading and on hard error.
 *   - data.recent_questions is always an array ([] when empty, never null/undefined).
 *   - data.corpus_stats always has total_docs / embedded_docs as numbers.
 *   - data.warnings is a string[] — non-empty means partial data; the UI may
 *     optionally surface these in development.
 */

import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '@/lib/apiFetch';
import type {
  WorkspaceDetailResponse,
  CorpusStats,
  RecentQuestion,
  JurisdictionInfo,
  QuickStats,
} from '@/types/workspaceDetail';

export type {
  WorkspaceDetailResponse,
  CorpusStats,
  RecentQuestion,
  JurisdictionInfo,
  QuickStats,
};

interface UseWorkspaceDetailResult {
  data: WorkspaceDetailResponse | null;
  loading: boolean;
  /** Hard error (network failure, 4xx/5xx). Partial-data issues surface in data.warnings instead. */
  error: string | null;
  refetch: () => void;
}

export function useWorkspaceDetail(
  workspaceId: string | undefined
): UseWorkspaceDetailResult {
  const [data, setData] = useState<WorkspaceDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!workspaceId) return;

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await apiFetch(`/api/workspaces/${workspaceId}/detail`);

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(
            (body as { error?: string }).error ??
              `Failed to load workspace detail (${res.status})`
          );
        }

        const json: WorkspaceDetailResponse = await res.json();

        if (!cancelled) {
          // Defensive normalisation — ensure all arrays are arrays
          setData({
            ...json,
            jurisdictions: json.jurisdictions ?? [],
            recent_questions: json.recent_questions ?? [],
            suggested_questions: json.suggested_questions ?? [],
            warnings: json.warnings ?? [],
            corpus_stats: {
              total_docs: json.corpus_stats?.total_docs ?? 0,
              embedded_docs: json.corpus_stats?.embedded_docs ?? 0,
              jurisdiction_stats: json.corpus_stats?.jurisdiction_stats ?? [],
              governing_bodies: json.corpus_stats?.governing_bodies ?? [],
            },
            quick_stats: {
              total_docs: json.quick_stats?.total_docs ?? 0,
              dated_docs: json.quick_stats?.dated_docs ?? 0,
              jurisdiction_count: json.quick_stats?.jurisdiction_count ?? 0,
              coverage_from: json.quick_stats?.coverage_from ?? null,
              coverage_to: json.quick_stats?.coverage_to ?? null,
              last_ingested_at: json.quick_stats?.last_ingested_at ?? null,
              created_by: json.quick_stats?.created_by ?? null,
            },
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Failed to load workspace'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [workspaceId, tick]);

  return { data, loading, error, refetch };
}
